/**
 * Website Configuration Detail Page
 *
 * Detail view for CyberSweep website configurations.
 * Fetches data from Gobodo/RDS (Audit_Website_Configuration__c).
 * Shows Related and Details tabs matching Salesforce layout.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  Globe,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Mail,
  Plus,
  Edit2,
  Trash2,
  Loader2,
} from 'lucide-react';
import {
  useWebsiteConfiguration,
  useAuditWebsitesByConfiguration,
  GobodoWebsiteConfig,
  PAWRecordDetail,
  PIIType,
} from '../../services/queries/cyberSweepQueries';
import { WEBSITE_CONFIGURATIONS, WEBSITE_CATEGORIES } from '../../data/websiteConfigurations';

/**
 * Convert static website configuration to match GobodoWebsiteConfig interface.
 * Used as fallback when Gobodo/RDS returns nothing for a static ID.
 */
function convertStaticToGobodoFormat(staticWebsite: typeof WEBSITE_CONFIGURATIONS[number]): GobodoWebsiteConfig {
  const category = WEBSITE_CATEGORIES.find(c => c.code === staticWebsite.category);
  return {
    id: staticWebsite.id,
    key: staticWebsite.id,
    name: staticWebsite.websiteCompany,
    url: staticWebsite.url || '',
    optOutUrl: '',
    optOutEmail: '',
    optOutMethod: '',
    notes: '',
    requirements: '',
    websiteCategory: category?.name || staticWebsite.category,
    websiteCategoryId: staticWebsite.category,
    autoEmail: false,
    automatedWebsite: false,
    isActive: staticWebsite.isActive,
    piiTypes: (staticWebsite.piiTypes || []) as PIIType[],
    ownerId: '',
    ownerName: '',
    createdById: '',
    createdByName: '',
    createdDate: '',
    lastModifiedById: '',
    lastModifiedByName: '',
    lastModifiedDate: '',
  };
}

interface WebsiteConfigurationDetailProps {
  websiteId: string;
  onBack: () => void;
  onPAWClick?: (pawId: string, csAuditId?: string) => void;
}

type TabType = 'related' | 'details';

const WebsiteConfigurationDetail: React.FC<WebsiteConfigurationDetailProps> = ({
  websiteId,
  onBack,
  onPAWClick,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('related');
  const [isEditing, setIsEditing] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch from Gobodo/RDS
  const { data: gobodoWebsite, isLoading } = useWebsiteConfiguration(websiteId);

  // Fallback to static data if Gobodo returns nothing
  const staticWebsite = WEBSITE_CONFIGURATIONS.find(w => w.id === websiteId);
  const websiteData: GobodoWebsiteConfig | null = gobodoWebsite
    ? gobodoWebsite
    : staticWebsite
      ? convertStaticToGobodoFormat(staticWebsite)
      : null;

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Not found state
  if (!websiteData) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Website Not Found
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ID "{websiteId}" not found.
          </p>
        </div>
      </div>
    );
  }

  // Display name from Gobodo/RDS or static data
  const displayName = websiteData.name || '';

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        {/* Title + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded">
              <Globe className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Audit Website Configuration
              </span>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {displayName}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={`px-2 py-1 text-xs font-medium border rounded transition-colors ${
                showNotes
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <FileText className="h-3.5 w-3.5 inline mr-1" />
              Note
            </button>
            <button
              onClick={() => {
                setIsEditing(!isEditing);
                if (!isEditing) setActiveTab('details');
              }}
              className={`px-2 py-1 text-xs font-medium border rounded transition-colors ${
                isEditing
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <Edit2 className="h-3.5 w-3.5 inline mr-1" />
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3.5 w-3.5 inline mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Notes Section (collapsible) */}
      {showNotes && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Add Note</h3>
            <button
              onClick={() => setShowNotes(false)}
              className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            className="w-full px-3 py-2 text-sm border border-yellow-300 dark:border-yellow-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400"
            rows={3}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => {
                setNoteText('');
                setShowNotes(false);
              }}
              className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log('[WebsiteConfig] Note saved:', noteText);
                // TODO: Save note via Gobodo API
                setNoteText('');
                setShowNotes(false);
              }}
              className="px-3 py-1 text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded"
            >
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{displayName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('[WebsiteConfig] Delete confirmed for:', websiteId);
                  // TODO: Implement delete via Gobodo API
                  setShowDeleteConfirm(false);
                  onBack();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('related')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'related'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Related
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Details
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'related' ? (
        <RelatedTab
          websiteId={websiteId}
          websiteName={displayName}
          onViewAllPAWs={() => {
            // TODO: Navigate to list view page for all PAW records
            console.log('[WebsiteConfig] View All PAWs clicked for:', websiteId, displayName);
          }}
          onPAWClick={(pawId) => {
            console.log('[WebsiteConfig] PAW clicked:', pawId);
            // Use the parent's onPAWClick to navigate to the PAW record
            if (onPAWClick) {
              onPAWClick(pawId);
            }
          }}
        />
      ) : (
        <DetailsTab
          gobodoData={websiteData}
          formatDate={formatDate}
          isEditing={isEditing}
          onSave={() => {
            console.log('[WebsiteConfig] Save changes');
            // TODO: Save via Gobodo API
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

// Related Tab Component
interface RelatedTabProps {
  websiteId: string;
  websiteName?: string;
  onViewAllPAWs?: () => void;
  onPAWClick?: (pawId: string) => void;
}

const RelatedTab: React.FC<RelatedTabProps> = ({ websiteId, websiteName, onViewAllPAWs, onPAWClick }) => {
  const { data: auditWebsites = [], isLoading: auditWebsitesLoading } = useAuditWebsitesByConfiguration(websiteId);

  // PAW records filtering by website requires backend implementation
  // For now, we disable this section until the backend provides proper traversal
  const pawRecordsEnabled = false; // TODO: Enable when backend supports website-specific PAW filtering
  const pawRecords: PAWRecordDetail[] = [];
  const pawRecordsLoading = false;
  const pawRecordsError = null;

  // Display helper - matches Audit Template pattern
  const getPawDisplayName = (record: PAWRecordDetail): string => {
    // Prefer the name field if it looks like a clean SF auto-number (PAW-00123)
    if (record.name && /^PAW-\d{5,6}$/.test(record.name)) {
      return record.name;
    }
    // Fall back to ID-based display
    const id = record.id ?? record.key ?? '';
    if (/^\d+$/.test(id)) {
      // Numeric ID - format as PAW-XXXXX
      return `PAW-${id.slice(-5).padStart(5, '0')}`;
    }
    return record.name || `PAW-${id.slice(0, 8)}`;
  };

  // Limit PAW records to 6 for display, show View All for more
  const displayedPawRecords = pawRecords.slice(0, 6);
  const hasMorePawRecords = pawRecords.length > 6;
  const totalPawCount = pawRecords.length;

  return (
    <div className="space-y-3">
      {/* Website Name (Audit Websites) Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Website Name</h3>
            {auditWebsitesLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            ) : (
              <span className="text-xs text-gray-500">({auditWebsites.length})</span>
            )}
          </div>
          <button className="px-1.5 py-0.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
            <Plus className="h-3 w-3 inline" /> New
          </button>
        </div>
        {auditWebsitesLoading ? (
          <div className="px-3 py-4 text-center">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-auto" />
            <span className="text-xs text-gray-500 mt-1 block">Loading audit websites...</span>
          </div>
        ) : auditWebsites.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            No audit websites found
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {auditWebsites.map((website) => (
              <div key={website.id} className="px-3 py-2">
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {website.name || website.id}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Performed Audit Websites Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-purple-500" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Performed Audit Websites</h3>
            {pawRecordsLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            ) : (
              <span className="text-xs text-gray-500">
                ({totalPawCount}{hasMorePawRecords ? '+' : ''})
              </span>
            )}
          </div>
        </div>

        {/* Subsection header */}
        <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/30 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">Performed Audit Website Name</span>
        </div>

        {/* PAW Records */}
        {pawRecordsLoading ? (
          <div className="px-3 py-4 text-center">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-auto" />
            <span className="text-xs text-gray-500 mt-1 block">Loading performed audit websites...</span>
          </div>
        ) : pawRecordsError ? (
          <div className="px-3 py-3 text-center text-xs text-red-500 dark:text-red-400">
            Error loading performed audit websites
          </div>
        ) : pawRecords.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            {pawRecordsEnabled
              ? `No performed audit websites found for ${websiteName || 'this configuration'}`
              : 'PAW records will be available after backend filtering is implemented'}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayedPawRecords.map((record) => (
                <div
                  key={record.id}
                  onClick={() => onPAWClick?.(record.id)}
                  className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <span className="text-sm text-blue-600 hover:underline dark:text-blue-400">
                    {getPawDisplayName(record)}
                  </span>
                </div>
              ))}
            </div>

            {/* View All footer */}
            {hasMorePawRecords && (
              <div className="px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 text-center">
                <button
                  onClick={() => onViewAllPAWs?.()}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  View All ({totalPawCount})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Automated Website Config Versions Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Automated Config Versions</h3>
            <span className="text-xs text-gray-500">(0)</span>
          </div>
          <button className="px-1.5 py-0.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
            <Plus className="h-3 w-3 inline" /> New
          </button>
        </div>
        <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
          No automated config versions found
        </div>
      </div>
    </div>
  );
};

// Details Tab Component
interface DetailsTabProps {
  gobodoData: GobodoWebsiteConfig | null | undefined;
  formatDate: (dateStr: string) => string;
  isEditing: boolean;
  onSave: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  gobodoData,
  formatDate,
  isEditing,
  onSave,
}) => {
  // Editable state for checkboxes
  const [editAutoEmail, setEditAutoEmail] = useState(gobodoData?.autoEmail ?? false);
  const [editAutomatedWebsite, setEditAutomatedWebsite] = useState(gobodoData?.automatedWebsite ?? false);
  const [editIsActive, setEditIsActive] = useState(gobodoData?.isActive ?? false);

  // Checkbox display helper
  const renderCheckbox = (
    label: string,
    value: boolean,
    editValue: boolean,
    setEditValue: (val: boolean) => void
  ) => (
    <div>
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{label}</label>
      {isEditing ? (
        <div className="mt-0.5">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={editValue}
              onChange={(e) => setEditValue(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
            <span className="ms-2 text-sm text-gray-700 dark:text-gray-300">{editValue ? 'Yes' : 'No'}</span>
          </label>
        </div>
      ) : (
        <div className="mt-0.5 flex items-center gap-1">
          {value ? (
            <>
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span className="text-sm text-green-600 dark:text-green-400">Yes</span>
            </>
          ) : (
            <>
              <XCircle className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">No</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-3">
        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
          {/* LEFT COLUMN */}
          <div className="space-y-3">
            {/* Website Company (Name) */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Website Company</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{gobodoData?.name || '-'}</p>
            </div>

            {/* URL */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">URL</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.url ? (
                  <a
                    href={gobodoData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400 inline-flex items-center gap-1"
                  >
                    {gobodoData.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : '-'}
              </p>
            </div>

            {/* Opt-Out URL */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Opt-Out URL</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.optOutUrl ? (
                  <a
                    href={gobodoData.optOutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400 inline-flex items-center gap-1"
                  >
                    {gobodoData.optOutUrl}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : '-'}
              </p>
            </div>

            {/* Opt-Out Email */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Opt-Out Email</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.optOutEmail ? (
                  <a
                    href={`mailto:${gobodoData.optOutEmail}`}
                    className="text-blue-600 hover:underline dark:text-blue-400 inline-flex items-center gap-1"
                  >
                    <Mail className="h-3 w-3" />
                    {gobodoData.optOutEmail}
                  </a>
                ) : '-'}
              </p>
            </div>

            {/* Opt-Out Method */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Opt-Out Method</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.optOutMethod || '-'}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Notes</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 whitespace-pre-wrap">
                {gobodoData?.notes || '-'}
              </p>
            </div>

            {/* Requirements */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requirements</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 whitespace-pre-wrap">
                {gobodoData?.requirements || '-'}
              </p>
            </div>

            {/* Website Category */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Website Category</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.websiteCategory || '-'}
              </p>
            </div>

            {/* Auto Email (Checkbox) */}
            {renderCheckbox('Auto Email', gobodoData?.autoEmail ?? false, editAutoEmail, setEditAutoEmail)}

            {/* Automated Website (Checkbox) */}
            {renderCheckbox('Automated Website', gobodoData?.automatedWebsite ?? false, editAutomatedWebsite, setEditAutomatedWebsite)}

            {/* Is Active (Checkbox) */}
            {renderCheckbox('Is Active', gobodoData?.isActive ?? false, editIsActive, setEditIsActive)}

            {/* Created By */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.createdByName
                  ? `${gobodoData.createdByName}, ${formatDate(gobodoData.createdDate)}`
                  : '-'}
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col justify-between">
            {/* Owner (Top) */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Owner</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.ownerName || '-'}
              </p>
            </div>

            {/* Last Modified By (Bottom) */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Modified By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {gobodoData?.lastModifiedByName
                  ? `${gobodoData.lastModifiedByName}, ${formatDate(gobodoData.lastModifiedDate)}`
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Save Button when editing */}
        {isEditing && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => {
                console.log('[WebsiteConfig] Saving changes:', {
                  autoEmail: editAutoEmail,
                  automatedWebsite: editAutomatedWebsite,
                  isActive: editIsActive,
                });
                onSave();
              }}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteConfigurationDetail;
